/**
 * boilerplate.js — the fixed legal text of an Osteo3d case report.
 *
 * TRANSCRIBED VERBATIM from the delivered reference report
 * "Case Report - O3D-2026-07-22-0001_c.pdf" (V1.3.3, 23 pages): the Agreement is
 * page 1, the Terms and Conditions are pages 22 and 23. That PDF is fully
 * rasterised — `pdftotext` yields nothing but page numbers — so this was read off
 * the rendered pages rather than extracted, and it should be proof-read against
 * the source once before the first report goes to a surgeon.
 *
 * This is legal text on a clinical document. Do not paraphrase it, do not
 * "tidy" it, and do not fix its typography: "the last two pages", the double
 * "the The", the missing space in "responsibility.(b)" and the lowercase
 * "case Report" are all as delivered. If Legal changes the wording, change it
 * here and nowhere else — every template pulls from this file.
 *
 * ── Known fidelity gaps vs the reference ────────────────────────────────────
 * · The reference sets its section headings as bold RUN-IN lead-ins on the same
 *   line as the paragraph ("NO WARRANTY FOR SERVICES To the extent…"). The
 *   layout engine has no run-in style, so they are `h2` headings on their own
 *   line. Same words, different typography.
 * · The reference numbers its contraindications and warnings "1." / "2." inside
 *   the paragraph flow. Reproduced literally as paragraphs rather than as
 *   `bullets`, which would prefix them with a dash and produce "- 1.".
 */

/**
 * Typography, MEASURED off the reference rather than chosen.
 *
 * Body size solved from a line that runs the full text block on page 22
 * ("Osteo3d® does not warrant and is not responsible for the outcome of any
 * surgery as a result of the provision of" = 186.7 mm): 10.3 pt, residual
 * 0.23 mm. Line pitch measured by horizontal row projection on the 200 dpi
 * raster — 4.19 mm over 22 rows on the Terms, 6.98 mm over 17 rows on the
 * Agreement, i.e. leading factors 1.153 and 1.92.
 *
 * They are genuinely different: the Agreement is set airy for a page a surgeon
 * signs, the Terms are set tight to fit two pages. A single global 1.35 sat
 * between the two and overflowed the Terms onto four pages, two nearly blank.
 */
export const AGREEMENT_STYLES = {
  p: { size: 10.3, leading: 1.92 },
  bullets: { size: 10.3, leading: 1.92 },
}

export const TERMS_STYLES = {
  p: { size: 10.3, leading: 1.153, spaceAfter: 1.6 },
  h2: { size: 10.3, leading: 1.153, spaceBefore: 2, spaceAfter: 0.6 },
}

/** Page 1 of the reference. */
export const AGREEMENT_BODY = [
  { kind: 'h1', text: 'Agreement' },
  {
    kind: 'p',
    text: 'For the case of patient ____________________, I, Dr. ______________________, have '
      + "received the patient's informed consent to share the patient data with Osteo3d® for "
      + 'Medical Model Creation and Online Surgical Planning to provide products like but not '
      + 'limited to Anatomical Models, Surgical Guides, Surgical Splints, Digitally Modified '
      + 'Models, Images, Measurements and other services that I deem necessary to aid in the '
      + 'surgical planning and the procedure. I hereby acknowledge and agree to the following',
  },
  {
    kind: 'bullets',
    items: [
      'I have been present for the Online Surgical Planning with Osteo3d® for the entire '
      + 'duration and am solely responsible for the clinical decisions made during the planning '
      + 'session and the associated products.',
      'I have thoroughly examined the images and/or measurements provided by Osteo3d® of the '
      + 'plan and the associated products and have verified that they meet my requirements and '
      + 'certify that no changes are needed.',
      'I acknowledge that all products are delivered non-sterile and that I am solely '
      + 'responsible for decontamination, cleaning and sterilization using the appropriate methods.',
      'I have read and understood the Terms and Conditions set forth on the last two pages of '
      + 'this case Report which are incorporated herein and agree to be bound by such Terms and '
      + 'Conditions.',
    ],
  },
  { kind: 'signature', labels: ['Surgeon Signature', 'Date'] },
  {
    kind: 'p',
    text: 'Please copy this page, sign and date, and email it to contactus@osteo3d.com',
  },
  {
    kind: 'p',
    text: 'Please note that if this agreement is not formally accepted prior to the surgery but '
      + 'the product and services are used, you nevertheless agree to be bound by the Terms and '
      + 'Conditions.',
  },
]

/** Page 22 of the reference — warranty, release, and the use information. */
export const TERMS_BODY_1 = [
  { kind: 'h1', text: 'TERMS AND CONDITIONS' },
  { kind: 'h2', text: 'NO WARRANTY FOR SERVICES' },
  {
    kind: 'p',
    text: 'To the extent permitted by applicable law, I acknowledge and understand that Osteo3d® '
      + 'does not warrant and is not responsible for the outcome of any surgery as a result of the '
      + 'provision of the Services provided to me or my employees, agents or designees in '
      + 'connection with this case.',
  },
  { kind: 'h2', text: 'RELEASE' },
  {
    kind: 'p',
    text: 'To the extent permitted by applicable law and except as otherwise provided herein, I '
      + 'hereby waive and release Osteo3d® from all Damages related to the Products and Services '
      + 'provided to me or my employees, agents or designees in connection with this case.',
  },
  { kind: 'h1', text: 'IMPORTANT USE INFORMATION' },
  {
    kind: 'p',
    text: 'The sale of these products and services is restricted by or on the order of a '
      + 'certified physician',
  },
  { kind: 'h2', text: 'INDICATIONS FOR USE:' },
  {
    kind: 'p',
    text: 'The Online Surgical Planning is intended for use as a software system and image '
      + 'segmentation system for the transfer of imaging information from a medical scanner such '
      + 'as a CT based system. The input data file is processed by the Osteo3d® software and the '
      + 'result is an output data file that may then be provided as digital models or used as '
      + 'input to a rapid prototyping portion of the system that produces physical outputs '
      + 'including Anatomical Models, Surgical Guides, Surgical Splints, Digitally Modified '
      + 'Models, Images, Measurements and other services for use in maxillofacial surgery. The '
      + 'Online Surgical Planning is also intended as a preoperative software tool for simulating '
      + '/ evaluating surgical treatment options.',
  },
  { kind: 'h2', text: 'CONTRAINDICATIONS:' },
  {
    kind: 'p',
    text: 'The outputs of the The Online Surgical Planning and the associated products should not '
      + 'be used if in any of the following occur:',
  },
  { kind: 'p', text: '1. Active infection is a contraindication for use of this device.' },
  {
    kind: 'p',
    text: "2. Significant changes to patient's anatomy have occurred since the medical scan used "
      + 'for product definition was obtained.',
  },
  { kind: 'h2', text: 'WARNINGS:' },
  {
    kind: 'p',
    text: '1. To avoid mix-ups and associated serious injury, the correct templates and guides '
      + 'must be verified and confirmed against patient identification prior to use using '
      + 'necessary methods.',
  },
  {
    kind: 'p',
    text: '2. Device(s) are single use only and designed for use with this patient only. To avoid '
      + 'risk of infection and serious injury, do not attempt to re-clean or re-sterilize or in '
      + 'any way re-use The Online Surgical Planning outputs.',
  },
  {
    kind: 'p',
    text: '3. Anatomical Models, Surgical Guides, Surgical Splints, Digitally Modified Models, '
      + 'Images, Measurements and other services are designed for this patient only. To avoid the '
      + 'potential for serious injury, Anatomical Models, Surgical Guides, Surgical Splints, '
      + 'Digitally Modified Models, Images, Measurements and other services should not be modified '
      + 'in any way.',
  },
  { kind: 'h2', text: 'PRECAUTIONS:' },
  {
    kind: 'p',
    text: 'Osteo3d® Anatomical Models, Surgical Guides, Surgical Splints, Digitally Modified '
      + 'Models are shipped in a non-sterilized state.  To avoid possibility of infection and '
      + 'serious injury, decontaminate, clean and sterilize using the appropriate methods.',
  },
]

/** Page 23 of the reference — indemnification and the limited warranty. */
export const TERMS_BODY_2 = [
  { kind: 'h1', text: 'TERMS AND CONDITIONS' },
  { kind: 'h2', text: 'INDEMNIFICATION' },
  {
    kind: 'p',
    text: 'To the extent permitted by applicable law, I hereby agree to indemnify and hold '
      + 'harmless Osteo3d®, its officers, directors, employees, successors and assignees, from and '
      + 'against any and all liabilities, demands, claims, actions or causes of action, judgments, '
      + 'proceedings or investigations, losses, fines, penalties, damages, costs and expenses, '
      + 'including, without limitation, reasonable attorneys\' fees (collectively, "Damages"), '
      + 'that may be sustained, suffered or incurred by any of them arising from, out of, in '
      + 'connection with, or by reason of (a) the death or injury of any person, or loss or damage '
      + 'to property, resulting from the Products used in connection with this case if such '
      + 'Products were (i) fabricated in accordance with the patient data for this case, including '
      + 'but not limited to any 3-D or other images (the "Patient Data") that were supplied to '
      + 'Osteo3d® by me or my employees, agents or designees, or (ii) used by me or my employees, '
      + 'agents or designees in a manner not intended or sanctioned by Osteo3d®, or (iii) altered, '
      + 'added to, or enhanced by me or my employees, agents or designees, (b) the negligence or '
      + 'willful misconduct of me or of my employees, agents or designees in the use of the '
      + 'Products, or (c) the design, plan and/or application or implementation of the Services.',
  },
  { kind: 'h2', text: 'LIMITED WARRANTY OF PRODUCTS' },
  {
    kind: 'p',
    text: 'Osteo3d® hereby warrants that upon dispatch of the products from Osteo3d®’s '
      + 'premises, such products will (i) be new and free from defects in material and '
      + 'workmanship, and (ii) conform to the Patient Data supplied to Osteo3d® and the case '
      + 'planning. Notwithstanding the foregoing, the warranties given by Osteo3d® for the '
      + 'products are hereby limited as follows, and no warranty is given by Osteo3d® with respect '
      + 'to any Products if any alterations, additions, or enhancements are made to the Products '
      + 'after delivery:',
  },
  {
    kind: 'p',
    text: '(a) Osteo3d® is not responsible for the accuracy of the Patient Data. Any damages '
      + 'caused by the products, or your inability use the products, resulting from inaccurate, '
      + 'incomplete or poor quality Patient Data shall be your sole responsibility.(b) Because of '
      + 'biological differences in individual patients, the Products cannot be, and are not, '
      + 'guaranteed to be 100% effective under all circumstances.  In addition, because Osteo3d® '
      + 'has no control of the condition under which the Products are used, nor any involvement in '
      + 'the diagnosis of the patient, the method of use or administration, or handling of the '
      + 'Products after delivery, Osteo3d® cannot and does not warrant either a good result or '
      + 'against an ill effect to the patient following use or application of the Products. Final '
      + 'determination of the suitability of the Products for the use contemplated by you or your '
      + 'employees, agents and designees is your sole responsibility, and Osteo3d® shall in no way '
      + 'be responsible for the suitability of the Products for any particular use or with any '
      + 'particular patient. (c) The Products, once shipped to you, are not in Osteo3d®’s '
      + 'control and will be put to uses under circumstances wholly unknown to Osteo3d®.  Any '
      + 'losses, injuries or Damages to persons or property caused by the alteration of, addition '
      + 'to, or enhancement to the Products after delivery are not the responsibility of Osteo3d®.',
  },
  {
    kind: 'p',
    text: 'This warranty is the exclusive warranty applicable to the Products. Osteo3d® '
      + 'SPECIFICALLY DISCLAIMS ALL OTHER WARRANTIES, EXPRESSED OR IMPLIED, INCLUDING BUT NOT '
      + 'LIMITED TO ANY IMPLIED WARRANTY OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE.',
  },
  {
    kind: 'p',
    text: 'In no event shall Osteo3d® be liable to you for any special, consequential, incidental '
      + 'or indirect Damages, however caused, on any theory of liability. In addition, in no event '
      + 'shall Osteo3d®’s maximum liability to you exceed the amounts actually paid by you, '
      + 'your patient, employees, agents or designees to Osteo3d® for the Products and Services '
      + 'provided pursuant to the Agreement to which these Terms and Conditions are attached.',
  },
]

// ── Titanium PSI ─────────────────────────────────────────────────────────────
/**
 * The Titanium PSI report's OWN agreement and terms.
 *
 * Team, 10 Oct 2026: "Agreement page , terms and conditions pages x 2 is not generated from the
 * case report builder for Titanium PSI ." Deepak, same day: "They are saying that there is a
 * separate template and we are not using the right one."
 *
 * They are right, and it is not only the product name. SOURCE: the team's own template,
 *   /Users/deepakrajk/Documents/Temp/GimpTemplates/
 *     Reference Ti PSI Case Report 2026 - O3D-2026-XX-XX-00XX_x_V1.3.1.xcf
 * md5 af21bb10b52c4e18766fcd581215116b, the md5 formats.js cites. Transcribed from its own TEXT
 * LAYERS, read in reading order out of tools/xcf-geometry/fixtures/
 * reference-ti-psi-case-report-2026/dump.json (143 text parasites in the file, 143 in the dump).
 * boilerplateTiPsi.test.js checks every line below against that dump, so this is the source's own
 * words and not a copy of them.
 *
 * It is the ONLY one of the three case-report templates whose legal pages carry real text: the
 * fibula template's carry 87 characters (headers), the orthognathic 0. The house constants above
 * were transcribed from a DIFFERENT document — a delivered ORTHOGNATHIC report, read off its
 * rendered pages — so the two are separate documents and differ in wording, measured:
 *
 *   · "Titanium Patient Specific Implants," is in the product list, and NOT always in the same
 *     place: after "Anatomical Models," in the agreement and in warning 3 and the precautions,
 *     but after "Surgical Splints," under INDICATIONS FOR USE. A blanket insertion rule would
 *     have put it in the wrong place there.
 *   · "the associated case report should not be used", where the house text reads "the
 *     associated products should not be used".
 *   · "CONTRAINDICATIONS" and "WARNINGS" carry no colon; the house headings do.
 *   · "decontaminate clean and sterilize", with no comma after "decontaminate".
 *   · The signature rules are longer: 23 and 25 underscores against the house's 20 and 22.
 *
 * Typography is NOT fixed, exactly as the house text is not: the missing full stop on the last
 * bullet, the double "the The", the lowercase "case Report" are all as the template has them.
 *
 * 🔴 TWO BLOCKS ARE NOT FROM THE TEMPLATE, because in the template they are pictures and the
 * words cannot be read out of them. Deepak decided on 10 Oct 2026 to take these from the house
 * constants rather than OCR them or wait for the team:
 *   · the whole INDEMNIFICATION page (template page 16, a 189 x 180 mm raster) — so Ti PSI
 *     reuses TERMS_BODY_2 unchanged;
 *   · NO WARRANTY FOR SERVICES and RELEASE on page 17 (a 187 x 28.7 mm raster).
 * Their opening 62 characters match the house text exactly, which is all the layer names carry.
 * The remainder is UNVERIFIED against the Ti PSI template. Do not read the test as proof of it.
 */
export const TI_PSI_AGREEMENT_BODY = [
  { kind: 'h1', text: 'Agreement' },
  {
    kind: 'p',
    text: 'For the case of patient _______________________, I, Dr. _________________________, have '
      + "received the patient's informed consent to share the patient data with Osteo3d® for "
      + 'Medical Model Creation and Online Surgical Planning to provide products like but not '
      + 'limited to Anatomical Models, Titanium Patient Specific Implants, Surgical Guides, '
      + 'Surgical Splints, Digitally Modified Models, Images, Measurements and other services '
      + 'that I deem necessary to aid in the surgical planning and the procedure. I hereby '
      + 'acknowledge and agree to the following',
  },
  {
    kind: 'bullets',
    items: [
      'I have been present for the Online Surgical Planning with Osteo3d® for the entire duration '
      + 'and am solely responsible for the clinical decisions made during the planning session and '
      + 'the associated products.',
      'I have thoroughly examined the images and/or measurements provided by Osteo3d® of the plan '
      + 'and the associated products and have verified that they meet my requirements and certify '
      + 'that no changes are needed.',
      'I acknowledge that all products are delivered non-sterile and that I am solely responsible '
      + 'for decontamination, cleaning and sterilization using the appropriate methods.',
      // No full stop after "Conditions" — the template has none. Not a typo to fix.
      'I have read and understood the Terms and Conditions set forth on the last two pages of '
      + 'this case Report which are incorporated herein and agree to be bound by such Terms and '
      + 'Conditions',
    ],
  },
  // The template carries these three as RASTERS ("Surgeon Signature", "Date", "Please copy this
  // page…", "Please note that if this agreement…"). Their layer names match the house wording,
  // so the house blocks stand in. See the 🔴 note above.
  { kind: 'signature', labels: ['Surgeon Signature', 'Date'] },
  {
    kind: 'p',
    text: 'Please copy this page, sign and date, and email it to contactus@osteo3d.com',
  },
  {
    kind: 'p',
    text: 'Please note that if this agreement is not formally accepted prior to the surgery but '
      + 'the product and services are used, you nevertheless agree to be bound by the Terms and '
      + 'Conditions.',
  },
]

/** Template page 17. Page 16 is TERMS_BODY_2 unchanged — see the 🔴 note above. */
export const TI_PSI_TERMS_BODY_1 = [
  // Raster in the template: TERMS AND CONDITIONS, NO WARRANTY FOR SERVICES, RELEASE.
  { kind: 'h1', text: 'TERMS AND CONDITIONS' },
  { kind: 'h2', text: 'NO WARRANTY FOR SERVICES' },
  {
    kind: 'p',
    text: 'To the extent permitted by applicable law, I acknowledge and understand that Osteo3d® '
      + 'does not warrant and is not responsible for the outcome of any surgery as a result of the '
      + 'provision of the Services provided to me or my employees, agents or designees in '
      + 'connection with this case.',
  },
  { kind: 'h2', text: 'RELEASE' },
  {
    kind: 'p',
    text: 'To the extent permitted by applicable law and except as otherwise provided herein, I '
      + 'hereby waive and release Osteo3d® from all Damages related to the Products and Services '
      + 'provided to me or my employees, agents or designees in connection with this case.',
  },
  // From here down, every line is the template's own text layers.
  { kind: 'h1', text: 'IMPORTANT USE INFORMATION' },
  {
    kind: 'p',
    text: 'The sale of these products and services is restricted by or on the order of a '
      + 'certified physician',
  },
  { kind: 'h2', text: 'INDICATIONS FOR USE:' },
  {
    kind: 'p',
    text: 'The Online Surgical Planning is intended for use as a software system and image '
      + 'segmentation system for the transfer of imaging information from a medical scanner such '
      + 'as a CT based system. The input data file is processed by the Osteo3d® software and the '
      + 'result is an output data file that may then be provided as digital models or used as '
      + 'input to a rapid prototyping portion of the system that produces physical outputs '
      + 'including Anatomical Models, Surgical Guides, Surgical Splints, Titanium Patient '
      + 'Specific Implants, Digitally Modified Models, Images, Measurements and other services '
      + 'for use in maxillofacial surgery. The Online Surgical Planning is also intended as a '
      + 'preoperative software tool for simulating / evaluating surgical treatment options.',
  },
  // No colon, unlike the house heading.
  { kind: 'h2', text: 'CONTRAINDICATIONS' },
  {
    kind: 'p',
    text: 'The outputs of the The Online Surgical Planning and the associated case report should '
      + 'not be used if in any of the following occur:',
  },
  { kind: 'p', text: '1. Active infection is a contraindication for use of this device.' },
  {
    kind: 'p',
    text: "2. Significant changes to patient's anatomy have occurred since the medical scan used "
      + 'for product definition was obtained.',
  },
  // No colon, unlike the house heading.
  { kind: 'h2', text: 'WARNINGS' },
  {
    kind: 'p',
    text: '1. To avoid mix-ups and associated serious injury, the correct templates and guides '
      + 'must be verified and confirmed against patient identification prior to use using '
      + 'necessary methods.',
  },
  {
    kind: 'p',
    text: '2. Device(s) are single use only and designed for use with this patient only. To avoid '
      + 'risk of infection and serious injury, do not attempt to re-clean or re-sterilize or in '
      + 'any way re-use The Online Surgical Planning outputs.',
  },
  {
    kind: 'p',
    text: '3. Anatomical Models, Titanium Patient Specific Implants, Surgical Guides, Surgical '
      + 'Splints, Digitally Modified Models, Images, Measurements and other services are designed '
      + 'for this patient only. To avoid the potential for serious injury, Anatomical Models, '
      + 'Titanium Patient Specific Implants, Surgical Guides, Surgical Splints, Digitally Modified '
      + 'Models, Images, Measurements and other services should not be modified in any way.',
  },
  { kind: 'h2', text: 'PRECAUTIONS:' },
  {
    kind: 'p',
    // "decontaminate clean" — no comma, as the template has it.
    text: 'Osteo3d® Anatomical Models, Titanium Patient Specific Implants, Surgical Guides, '
      + 'Surgical Splints, Digitally Modified Models are shipped in a non-sterilized state. To '
      + 'avoid possibility of infection and serious injury, decontaminate clean and sterilize '
      + 'using the appropriate methods.',
  },
]
